import React, { createContext, useContext, useState, useEffect } from 'react'

export type AuthContextType = {
  authenticated: boolean
  authorized: boolean
  setAuthenticated: (authenticated: boolean) => void
  setStatusCode: (statusCode: number) => void
  logout: (redirectUri?: string) => void
}

export type AuthBoundaryProps = {
  authUrl?: string
  redirect401?: string
  redirect403?: string
  children: React.ReactNode
}

const AuthContext = createContext({} as AuthContextType)

function AuthBoundary({
  authUrl = `${window.location.origin}/auth`,
  redirect401 = `${authUrl}/auth`,
  redirect403 = `${authUrl}/auth`,
  children,
}: AuthBoundaryProps) {
  const [statusCode, setStatusCode] = useState<number | null>()
  // prevStatusCode is used to prevent infinite loops.
  const [prevStatusCode, setPrevStatusCode] = useState<number | null>()
  const [authenticated, setAuthenticated] = useState<boolean>(true)
  const [authorized, setAuthorized] = useState<boolean>(true)

  const logout = (redirectUri?: string) => {
    if (redirectUri != null) {
      window.location.href = `${authUrl}/logout?redirect_uri=${redirectUri}`
    } else {
      window.location.href = `${authUrl}/logout`
    }
  }

  useEffect(() => {
    if (statusCode == null) return
    if (statusCode === prevStatusCode) return
    if (statusCode === 401) {
      setPrevStatusCode(statusCode)
      setAuthenticated(false)
      setAuthorized(false)
      window.location.href = redirect401
    } else if (statusCode === 403) {
      setPrevStatusCode(statusCode)
      setAuthenticated(true)
      setAuthorized(false)
      window.location.href = redirect403
    } else if (statusCode >= 200 && statusCode < 300) {
      setPrevStatusCode(statusCode)
      setAuthenticated(true)
      setAuthorized(true)
    }
  }, [statusCode])

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        authorized,
        setAuthenticated,
        setStatusCode,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => useContext(AuthContext)

export { AuthBoundary, useAuth }
